import React, { useState, useMemo, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { greyText, lightGreyOutline, lightGreyText } from "../../../styles";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import {
  CA100_DISCLOSURE_VALUE_MAP,
  CA100_DISCLOSURE_VALUE_MAP_TOP_LEVEL,
  CA100_disclosureMetrics,
  Ca100DisclosureIndicatorValue,
  LobbyingTreeNode,
  DisclosureTreeNode,
  CA100_lobbyingMetrics,
  AlignmentGrade,
  AlignmentGradeScoreMap,
} from "./CA100Constants";
import {
  Ca100Package,
  PublicMetric,
} from "../../../models/CompanyprofileModels";
import { fallbackRetrievePct, getCA100Metric } from "./util";

type MetricsFor<T> = T extends DisclosureTreeNode
  ? CA100_disclosureMetrics
  : T extends LobbyingTreeNode
  ? CA100_lobbyingMetrics
  : never;

const StyledRowContainer = styled(RowContainer)`
  width: 90%;
  padding-bottom: 10px;
  border-bottom: 1px solid ${lightGreyOutline};
`;

const getIconFromValue = (value: string, isChild = false): string => {
  const indicatorValue = value as Ca100DisclosureIndicatorValue;
  const map = isChild
    ? CA100_DISCLOSURE_VALUE_MAP
    : CA100_DISCLOSURE_VALUE_MAP_TOP_LEVEL;
  return (
    map[indicatorValue] || map[Ca100DisclosureIndicatorValue.NotApplicable]
  );
};

const getIndicatorValueFromPct = (
  score: number
): Ca100DisclosureIndicatorValue => {
  if (score > 70) {
    return Ca100DisclosureIndicatorValue.Y;
  }
  if (score > 40) {
    return Ca100DisclosureIndicatorValue.P;
  }
  return Ca100DisclosureIndicatorValue.N;
};

const getIconFromValueLobbying = (value: string, isChild = false): string => {
  const score =
    AlignmentGradeScoreMap[value as AlignmentGrade] ||
    fallbackRetrievePct(value, isChild);

  const indicatorValue = getIndicatorValueFromPct(score);

  return getIconFromValue(indicatorValue, isChild);
};

export const CA100Accordion = <
  T extends LobbyingTreeNode | DisclosureTreeNode
>({
  metricsData,
  node,
  dataPackage,
}: {
  metricsData: PublicMetric[];
  node: T;
  dataPackage: Ca100Package;
}): JSX.Element => {
  const legendRef = useRef<HTMLDivElement | null>(null);
  const [isActive, setIsActive] = useState(false);

  const { metricName, value } = useMemo(() => {
    return getCA100Metric<MetricsFor<T>>(
      node.id as MetricsFor<T>,
      dataPackage,
      metricsData
    );
  }, [node.id, metricsData]);

  const scrollToLegend = (): void => {
    legendRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (isActive) {
      scrollToLegend();
    }
  }, [isActive]);

  return (
    <StyledRowContainer ref={legendRef} jc="flex-start" ai="flex-start">
      <FlexibleContainer fd="column" ai="flex-start">
        <ElementContainer ai="center" jc="flex-start">
          <IconContainer size={35}>
            <Icon
              src={
                dataPackage === Ca100Package.DISCLOSURE
                  ? getIconFromValue(value)
                  : getIconFromValueLobbying(value)
              }
            />
          </IconContainer>

          <ToggleAccordionBlueText
            textActive={metricName}
            textInactive={metricName}
            handleChange={(): void => setIsActive(!isActive)}
            color={greyText}
            fontSize={14}
            bold={false}
          />
        </ElementContainer>

        {/* Accordion content */}
        {isActive &&
          node.children?.map((childNode) => (
            <ChildLevelAccordionItem<T>
              key={childNode.id}
              node={childNode as T}
              metricsData={metricsData}
              dataPackage={dataPackage}
            />
          ))}
      </FlexibleContainer>
    </StyledRowContainer>
  );
};

export const ChildLevelAccordionItem = <
  T extends LobbyingTreeNode | DisclosureTreeNode
>({
  node,
  metricsData,
  dataPackage,
  level = 2,
}: {
  node: T;
  metricsData: PublicMetric[];
  dataPackage: Ca100Package;
  level?: 2 | 3;
}): JSX.Element => {
  const legendRef = useRef<HTMLDivElement | null>(null);
  const [isChildActive, setIsChildActive] = useState(false);

  const { metricName, value } = useMemo(() => {
    return getCA100Metric<MetricsFor<T>>(
      node.id as MetricsFor<T>,
      dataPackage,
      metricsData
    );
  }, [node.id, metricsData]);

  const scrollToLegend = (): void => {
    legendRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (isChildActive) {
      scrollToLegend();
    }
  }, [isChildActive]);

  return (
    <FlexibleContainer ref={legendRef} p="0 32px" fd="column" ai="flex-start">
      <ElementContainer ai="center" jc="flex-start">
        <IconContainer size={level === 2 ? 30 : 28}>
          <Icon
            src={
              dataPackage === Ca100Package.DISCLOSURE
                ? getIconFromValue(value, true)
                : getIconFromValueLobbying(value, true)
            }
            alt="question-status-icon"
          />
        </IconContainer>

        {node.children ? (
          <ToggleAccordionBlueText
            textActive={metricName}
            textInactive={metricName}
            handleChange={(): void => setIsChildActive(!isChildActive)}
            color={greyText}
            fontSize={14}
            bold={false}
          />
        ) : (
          <CompassParagraph
            color={level === 2 ? greyText : lightGreyText}
            fontSize={level === 2 ? 13 : 12}
          >
            {metricName}
          </CompassParagraph>
        )}
      </ElementContainer>

      {isChildActive &&
        node.children?.map((childNode) => (
          <ChildLevelAccordionItem<T>
            key={childNode.id}
            node={childNode as T}
            metricsData={metricsData}
            level={3}
            dataPackage={dataPackage}
          />
        ))}
    </FlexibleContainer>
  );
};
